import { AISettings, AIEvaluationResult } from '../types';
import { isElectron } from './DatabaseFactory';
import { WebAIEvaluationService } from './WebAIEvaluationService';

const PWA_SETTINGS_KEY = 'flash-e-ai-settings';
const PWA_API_KEY_KEY = 'flash-e-ai-api-key';

// Holds the API key for the current session when "remember key" is off
let _sessionApiKey: string | undefined;

export function loadPwaAISettings(): AISettings & { rememberKey: boolean } {
  try {
    const stored = JSON.parse(localStorage.getItem(PWA_SETTINGS_KEY) ?? '{}');
    const rememberKey = !!stored.rememberKey;
    const apiKey = rememberKey
      ? (localStorage.getItem(PWA_API_KEY_KEY) ?? undefined)
      : _sessionApiKey;
    return {
      provider: stored.provider ?? 'disabled',
      temperature: stored.temperature ?? 0.3,
      apiKey,
      rememberKey,
    };
  } catch {
    return { provider: 'disabled', temperature: 0.3, rememberKey: false };
  }
}

export function savePwaAISettings(settings: AISettings, rememberKey: boolean): void {
  localStorage.setItem(PWA_SETTINGS_KEY, JSON.stringify({
    provider: settings.provider,
    temperature: settings.temperature ?? 0.3,
    rememberKey,
  }));

  if (rememberKey && settings.apiKey) {
    localStorage.setItem(PWA_API_KEY_KEY, settings.apiKey);
    _sessionApiKey = undefined;
  } else {
    localStorage.removeItem(PWA_API_KEY_KEY);
    _sessionApiKey = settings.apiKey;
  }
}

export async function loadAISettings(): Promise<AISettings> {
  if (isElectron()) {
    return window.electronAPI.getAISettings();
  }
  const { rememberKey: _r, ...settings } = loadPwaAISettings();
  return settings;
}

export async function evaluateAnswer(
  settings: AISettings,
  userAnswer: string,
  referenceAnswer: string,
  question: string,
  language: string
): Promise<AIEvaluationResult> {
  if (isElectron()) {
    return window.electronAPI.evaluateAnswer(userAnswer, referenceAnswer, question, language);
  }
  return new WebAIEvaluationService(settings).evaluateAnswer(
    userAnswer, referenceAnswer, question, language
  );
}

export async function testAIConnection(
  settings: AISettings
): Promise<{ success: boolean; error?: string }> {
  if (isElectron()) {
    return window.electronAPI.testAIConnection();
  }
  return new WebAIEvaluationService(settings).testConnection();
}
